import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';

import { Typography } from '@app/components';
import { EmailApi } from '@app/domain/email/api/EmailApi';
import { CustomerTimelineEntryOutDto, ETimelineRecordType } from '@app/swagger-types';
import { TimelineEntryCard } from './TimelineEntryCard';
import { RenderedMailDialog } from './RenderedMailDialog';

type RecordRef = { recordType: ETimelineRecordType; recordId: string };

// admin-emailbeheer blok A stap 5, opdracht §5 "Tijdlijn - het belangrijkste onderdeel".
// Already sorted newest-first by the backend (EmailCustomerAdminFacadeImpl.buildTimeline).
export const CustomerTimeline: React.FC<{
  userId: string;
  timeline: CustomerTimelineEntryOutDto[];
  onActionComplete: () => void;
}> = ({ userId, timeline, onActionComplete }) => {
  const [viewing, setViewing] = useState<RecordRef | null>(null);

  const viewQuery = useQuery({
    queryKey: ['admin-email-customer-render', userId, viewing?.recordType, viewing?.recordId],
    queryFn: () => {
      if (!viewing) {
        return Promise.resolve(undefined);
      }
      return EmailApi.adminViewRenderedEmail(userId, viewing.recordType, viewing.recordId);
    },
    enabled: !!viewing,
  });

  const resendMutation = useMutation({
    mutationFn: ({ recordType, recordId, reason }: RecordRef & { reason: string }) =>
      EmailApi.adminResendEmail(userId, recordType, recordId, { reason }),
    onSuccess: onActionComplete,
  });

  const cancelMutation = useMutation({
    mutationFn: ({ scheduleId, reason }: { scheduleId: string; reason: string }) =>
      EmailApi.adminCancelScheduledEmail(userId, scheduleId, { reason }),
    onSuccess: onActionComplete,
  });

  if (!timeline.length) {
    return (
      <Typography variant="m" className="text-gray-400">
        Nothing on the timeline yet.
      </Typography>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {timeline.map((entry, i) => (
        <TimelineEntryCard
          key={entry.recordId ?? i}
          entry={entry}
          onView={() => {
            if (entry.recordType && entry.recordId) {
              setViewing({ recordType: entry.recordType, recordId: entry.recordId });
            }
          }}
          onResend={(reason) => {
            if (entry.recordType && entry.recordId) {
              resendMutation.mutate({ recordType: entry.recordType, recordId: entry.recordId, reason });
            }
          }}
          onCancel={(reason) => {
            if (entry.recordId) {
              cancelMutation.mutate({ scheduleId: entry.recordId, reason });
            }
          }}
          resendLoading={resendMutation.isLoading && resendMutation.variables?.recordId === entry.recordId}
          cancelLoading={cancelMutation.isLoading && cancelMutation.variables?.scheduleId === entry.recordId}
        />
      ))}

      <RenderedMailDialog
        open={!!viewing}
        onClose={() => setViewing(null)}
        loading={viewQuery.isFetching}
        subject={viewQuery.data?.subject}
        template={viewQuery.data?.template}
        html={viewQuery.data?.html}
      />
    </div>
  );
};
